import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { usePagination } from '../../hooks/ui/usePagination';
import {
  fmt,
  Triangle,
  ResetButton,
  SearchBar,
  Pager,
  rangeText,
  MethodTag,
  ruleValue,
} from './PointShared';
import styles from './PointV2.module.css';

/* 팜포인트 적립 기준 (조회 전용) — Figma 386:13312 / 456:21468(카테고리별), 409:17250(상품별), 409:18035(결제금액 구간별) */

const SAMPLE_PRODUCTS = [
  ['종합비타민 이뮨', '88060800011027'],
  ['오메가3 트리플', '8809348670102'],
  ['비타민C 1000', '8809348670119'],
  ['멀티비타민 데일리', '8809348670133'],
  ['노바스크정', '8806001234567'],
  ['오트리빈', '8806001234574'],
];
const makeProducts = (count, overrides = {}) =>
  Array.from({ length: count }, (_, i) => ({
    name: SAMPLE_PRODUCTS[i % SAMPLE_PRODUCTS.length][0],
    barcode: SAMPLE_PRODUCTS[i % SAMPLE_PRODUCTS.length][1],
    override: overrides[i] ?? null,
  }));

// API 미연동 — 백엔드 명세 v2 H-5 (GET /point/earn-rules/{brn}/categories)
const MOCK_CATEGORIES = {
  total: 6,
  items: [
    { category: '비타민계', product_count: 18, method: 'FIXED', value: 100, products: makeProducts(18, { 0: { method: 'FIXED', value: 2000 } }) },
    { category: '혈압강하제', product_count: 12, method: 'RATE', value: 3, products: makeProducts(12) },
    { category: '진해거담제', product_count: 12, method: 'RATE', value: 3, products: makeProducts(12, { 5: { method: 'FIXED', value: 2000 } }) },
    { category: '소화제', product_count: 9, method: 'FIXED', value: 100, products: makeProducts(9) },
    { category: '진통제', product_count: 14, method: 'RATE', value: 3, products: makeProducts(14) },
    { category: '유산균', product_count: 7, method: 'FIXED', value: 200, products: makeProducts(7) },
  ],
};

// API 미연동 — 백엔드 명세 v2 H-5 (GET /point/earn-rules/{brn}/products)
const PRODUCT_NAMES = ['가스활명수큐액', '판콜에이내복액', '베아제정', '훼스탈플러스정', '타이레놀정', '센트룸 멀티', 'Omega-3 1000', 'Vitamin D 2000IU', '락토핏 골드', '닥터베아제'];
const MOCK_PRODUCTS = {
  total: 137,
  items: Array.from({ length: 137 }, (_, i) => ({
    name: PRODUCT_NAMES[i % PRODUCT_NAMES.length],
    barcode: String(8806010010012 + i),
    category: i % 3 === 0 ? '건기식' : '부외품',
    method: i % 2 === 0 ? 'RATE' : 'FIXED',
    value: i % 2 === 0 ? 3 : 1500,
  })),
};

// API 미연동 — 백엔드 명세 v2 H-5 (GET /point/earn-rules/{brn}/amount-tiers)
const MOCK_TIERS = {
  total: 3,
  items: [
    { min_amount: 10000, card_rate: 5, cash_rate: 3, method: 'RATE' },
    { min_amount: 15000, card_rate: 1, cash_rate: 1, method: 'RATE' },
    { min_amount: 20000, card_rate: 3, cash_rate: 5, method: 'RATE' },
  ],
};

const TABS = [
  { key: 'category', label: '카테고리별', count: MOCK_CATEGORIES.total },
  { key: 'product', label: '상품별', count: MOCK_PRODUCTS.total },
  { key: 'tier', label: '결제금액 구간별', count: MOCK_TIERS.total },
];

const PREVIEW_COUNT = 5;
const PRODUCT_PAGE_SIZE = 20;
const CONSONANTS = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const LATIN = 'abcdefghijklmnopqrstuvwxyz'.split('');
const CHOSEONG = ['ㄱ', 'ㄱ', 'ㄴ', 'ㄷ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅂ', 'ㅅ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

/** 첫 글자 인덱스: 한글 → 초성(쌍자음은 홑자음), 영문 → 소문자 */
export const initialOf = name => {
  const c = (name || '').charCodeAt(0);
  if (c >= 0xac00 && c <= 0xd7a3) return CHOSEONG[Math.floor((c - 0xac00) / 588)];
  return (name || '').charAt(0).toLowerCase();
};

const manwon = n => `${fmt(n / 10000)}만원 이상`;

/* ── 카테고리별 ── */
const CategoryTab = () => {
  const [keyword, setKeyword] = useState('');
  const [openSet, setOpenSet] = useState(() => new Set([MOCK_CATEGORIES.items[0].category]));
  const [showAll, setShowAll] = useState(() => new Set());

  const rows = MOCK_CATEGORIES.items.filter(c => c.category.includes(keyword.trim()));
  const toggle = (set, setter, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  return (
    <>
      <div className={styles.searchContainer}>
        <SearchBar value={keyword} onChange={setKeyword} placeholder="카테고리 검색" />
      </div>
      <div className={styles.table}>
        <div className={styles.thead}>
          <div className={styles.catCol}>카테고리</div>
          <div className={styles.cell}>상품명</div>
          <div className={styles.cell}>상품 코드</div>
          <div className={`${styles.cell} ${styles.right} ${styles.cellNoBorder}`}>포인트</div>
        </div>
        {rows.map(cat => {
          const open = openSet.has(cat.category);
          // 시안 annotation: 카테고리와 다른 적립(개별)이 있는 상품이 위로
          const sorted = [...cat.products].sort((a, b) => (b.override ? 1 : 0) - (a.override ? 1 : 0));
          const visible = showAll.has(cat.category) ? sorted : sorted.slice(0, PREVIEW_COUNT);
          const hidden = sorted.length - visible.length;
          return (
            <div key={cat.category} className={styles.catRow} onClick={() => toggle(openSet, setOpenSet, cat.category)}>
              <div className={`${styles.catCol} ${open ? '' : styles.catColCollapsed}`}>
                <div className={styles.catName}>
                  <span className={styles.catNameText}>{cat.category}</span>
                  <span style={{ display: 'flex', color: '#a1a1aa' }}>
                    <Triangle dir={open ? 'up' : 'down'} size={20} />
                  </span>
                </div>
              </div>
              <div className={styles.subsections}>
                <div className={styles.subRow}>
                  <div className={styles.subSummary}>
                    <span className={styles.subCount}>총 {fmt(cat.product_count)}개</span>
                    <span className={styles.subValue}>{ruleValue(cat.method, cat.value)}</span>
                    <MethodTag method={cat.method} />
                  </div>
                </div>
                {open &&
                  visible.map((p, i) => (
                    <div key={`${p.barcode}-${i}`} className={styles.subRow} onClick={e => e.stopPropagation()}>
                      <div className={styles.cell}>{p.name}</div>
                      <div className={styles.cell}>{p.barcode}</div>
                      <div className={`${styles.cell} ${styles.right} ${styles.cellNoBorder}`}>
                        {p.override ? (
                          <span className={styles.overridePill}>개별 {ruleValue(p.override.method, p.override.value)}</span>
                        ) : (
                          `카테고리 ${ruleValue(cat.method, cat.value)} 적용`
                        )}
                      </div>
                    </div>
                  ))}
                {open && hidden > 0 && (
                  <button
                    type="button"
                    className={`${styles.subRow} ${styles.moreRow}`}
                    onClick={e => {
                      e.stopPropagation();
                      toggle(showAll, setShowAll, cat.category);
                    }}
                  >
                    상품 {fmt(hidden)}개 더 보기
                    <ChevronDown size={18} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

/* ── 상품별 ── */
const ProductTab = () => {
  const [keyword, setKeyword] = useState('');
  const [initial, setInitial] = useState(null);

  const rows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return MOCK_PRODUCTS.items.filter(
      p =>
        (!kw || p.name.toLowerCase().includes(kw) || p.barcode.includes(kw)) &&
        (!initial || initialOf(p.name) === initial)
    );
  }, [keyword, initial]);

  const { currentPage, setCurrentPage, totalPages, sliceItems } = usePagination({
    totalItems: rows.length,
    pageSize: PRODUCT_PAGE_SIZE,
  });

  const reset = () => {
    setKeyword('');
    setInitial(null);
    setCurrentPage(1);
  };

  return (
    <>
      <div className={styles.block}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={keyword}
            onChange={v => {
              setKeyword(v);
              setCurrentPage(1);
            }}
            placeholder="상품명 • 바코드 검색"
          />
          <ResetButton onClick={reset} />
        </div>
        <div className={styles.indexBar}>
          <div className={styles.indexLetters}>
            {[...CONSONANTS, ...LATIN].map(ch => (
              <button
                key={ch}
                type="button"
                className={`${styles.letter} ${initial === ch ? styles.letterActive : ''}`}
                onClick={() => {
                  setInitial(initial === ch ? null : ch);
                  setCurrentPage(1);
                }}
              >
                {ch}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.table}>
        <div className={styles.thead}>
          <div className={styles.cell} style={{ minWidth: 180 }}>상품명</div>
          <div className={styles.cell} style={{ minWidth: 140 }}>바코드</div>
          <div className={styles.cell} style={{ minWidth: 180 }}>카테고리</div>
          <div className={styles.cell} style={{ minWidth: 120, maxWidth: 140 }}>적립</div>
          <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>방식</div>
        </div>
        {rows.length === 0 ? (
          <div className={styles.emptyRow}>검색 결과가 없습니다</div>
        ) : (
          sliceItems(rows).map((p, i) => (
            <div key={`${p.barcode}-${i}`} className={styles.tr}>
              <div className={styles.cell} style={{ minWidth: 180 }}>{p.name}</div>
              <div className={styles.cell} style={{ minWidth: 140 }}>{p.barcode}</div>
              <div className={styles.cell} style={{ minWidth: 180 }}>{p.category}</div>
              <div
                className={`${styles.cell} ${styles.semibold} ${p.method === 'RATE' ? styles.blue : styles.dark}`}
                style={{ minWidth: 120, maxWidth: 140 }}
              >
                {ruleValue(p.method, p.value)}
              </div>
              <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>
                <MethodTag method={p.method} />
              </div>
            </div>
          ))
        )}
      </div>
      <Pager
        info={`전체 ${fmt(rows.length)}개 · ${rangeText(currentPage, PRODUCT_PAGE_SIZE, rows.length)} 표시`}
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />
    </>
  );
};

/* ── 결제금액 구간별 ── */
const TierTab = () => (
  <div className={styles.table}>
    <div className={styles.thead}>
      <div className={styles.cell} style={{ minWidth: 180 }}>기준 금액</div>
      <div className={styles.cell} style={{ minWidth: 140 }}>카드 결제</div>
      <div className={styles.cell} style={{ minWidth: 180 }}>현금 결제</div>
      <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>방식</div>
    </div>
    {MOCK_TIERS.items.map(t => (
      <div key={t.min_amount} className={styles.tr}>
        <div className={styles.cell} style={{ minWidth: 180 }}>{manwon(t.min_amount)}</div>
        <div className={styles.cell} style={{ minWidth: 140 }}>{ruleValue(t.method, t.card_rate)}</div>
        <div className={styles.cell} style={{ minWidth: 180 }}>{ruleValue(t.method, t.cash_rate)}</div>
        <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>
          <MethodTag method={t.method} />
        </div>
      </div>
    ))}
  </div>
);

const PointEarnRules = () => {
  const [tab, setTab] = useState('category');

  return (
    <div className={styles.page}>
      <div className={styles.tabMenu}>
        <div className={styles.tabs} role="tablist">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span>{t.label}</span>
              <span>{t.count}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={styles.rulesContent}>
        {tab === 'category' && <CategoryTab />}
        {tab === 'product' && <ProductTab />}
        {tab === 'tier' && <TierTab />}
      </div>
    </div>
  );
};

export default PointEarnRules;
